import {
  Channel,
  ChannelFilters,
  ChannelSort,
  DefaultGenerics,
  StreamChat,
} from 'stream-chat'
import { RETRY_CONFIG } from '../constants'

interface RetryConfig {
  maxRetries: number
  baseDelayMs: number
  maxDelayMs: number
}

const isRateLimitError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'status' in error) {
    return (error as { status: number }).status === 429
  }
  return false
}

const getRetryAfterMs = (error: unknown): number | null => {
  if (
    error &&
    typeof error === 'object' &&
    'headers' in error &&
    error.headers &&
    typeof error.headers === 'object' &&
    'retry-after' in error.headers
  ) {
    const retryAfter = Number(
      (error.headers as Record<string, string>)['retry-after']
    )
    if (!Number.isNaN(retryAfter)) return retryAfter * 1000
  }
  return null
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = RETRY_CONFIG
): Promise<T> {
  let lastError: unknown

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn()
    } catch (error) {
      lastError = error

      if (attempt === config.maxRetries) break

      let delayMs: number
      const retryAfterMs = getRetryAfterMs(error)

      if (retryAfterMs !== null) {
        delayMs = Math.min(retryAfterMs, config.maxDelayMs)
      } else if (isRateLimitError(error)) {
        delayMs = Math.min(config.baseDelayMs * 2 ** attempt, config.maxDelayMs)
      } else {
        // Non-rate-limit errors are not retried
        break
      }

      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, delayMs)
      })
    }
  }

  throw lastError
}

export async function queryChannelsWithRetry(
  client: StreamChat,
  filter: ChannelFilters,
  sort?: ChannelSort,
  options?: { limit?: number; offset?: number; state?: boolean }
): Promise<Channel<DefaultGenerics>[]> {
  return withRetry(() => client.queryChannels(filter, sort ?? {}, options))
}

export async function markReadWithRetry(
  channel: Channel<DefaultGenerics>
): Promise<void> {
  await withRetry(() => channel.markRead())
}
